import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/shared/components/ui/alert-dialog'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/shared/components/ui/form'
import { Input } from '@/shared/components/ui/input'
import { Playbook } from '@/types/playbook.old'
import { zodResolver } from '@hookform/resolvers/zod'
import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import * as z from 'zod'

const renameSchema = z.object({
  title: z
    .string()
    .min(1, 'Title is required')
    .max(100, 'Title must be less than 100 characters')
    .trim(),
})

export type RenameFormValues = z.infer<typeof renameSchema>

type PlaybookRenameModalProps = {
  playbook?: Pick<Playbook, 'title'> | null
  defaultValues?: {
    title: string
  }
  onSubmit: (values?: RenameFormValues) => Promise<void>
  isOpen: boolean
  onOpenChange: (open: boolean) => void
}

export function PlaybookRenameModal({
  playbook,
  defaultValues,
  onSubmit,
  isOpen,
  onOpenChange,
}: PlaybookRenameModalProps) {
  const form = useForm<RenameFormValues>({
    resolver: zodResolver(renameSchema),
    defaultValues: {
      title: playbook?.title || '',
      ...defaultValues,
    },
  })

  const {
    formState: { isSubmitting, isDirty },
    reset,
  } = form

  useEffect(() => {
    if (isOpen) {
      reset({
        title: playbook?.title || '',
        ...defaultValues,
      })
    }
  }, [defaultValues, reset, isOpen, playbook?.title])

  return (
    <AlertDialog open={isOpen} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <AlertDialogHeader>
            <AlertDialogTitle>Rename playbook</AlertDialogTitle>
            <AlertDialogDescription>
              Enter a new name for
              {playbook?.title ? ` "${playbook.title}"` : ' this playbook'}.
            </AlertDialogDescription>
          </AlertDialogHeader>

          <div className="py-4">
            <Form {...form}>
              <FormField
                control={form.control}
                name="title"
                render={({ field }) => (
                  <FormItem autoFocus>
                    <FormLabel>Playbook name</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        placeholder="Enter playbook name"
                        className="w-full"
                        autoFocus
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </Form>
          </div>

          <AlertDialogFooter>
            <AlertDialogCancel disabled={isSubmitting} type="button">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              disabled={isSubmitting || !isDirty}
              type="submit"
            >
              {isSubmitting ? 'Renaming…' : 'Rename'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </form>
      </AlertDialogContent>
    </AlertDialog>
  )
}
